/**
 * Admin API — HMAC-signed, bearer-gated. Mirrors api.ts but with admin_token.
 * Default admin: admin / vpnee2026 (change in backend, then enable 2FA).
 */
import { ENDPOINT_V2 } from './api';

const APP_ID = 'vpnee-web';
const APP_SECRET = 'e9c8e363ea09cab1ccff9463093ea52ebe9898fd11bb15f476d31add35920d9f';
const TOKEN_KEY = 'vpnee_admin_token';
const USER_KEY  = 'vpnee_admin_user';

const enc = new TextEncoder();
const toHex = (b: ArrayBuffer) => [...new Uint8Array(b)].map(x => x.toString(16).padStart(2, '0')).join('');
const sha256 = async (m: string) => toHex(await crypto.subtle.digest('SHA-256', enc.encode(m)));
async function hmac(secret: string, msg: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return toHex(await crypto.subtle.sign('HMAC', key, enc.encode(msg)));
}
function nonce(): string { const a = new Uint8Array(16); crypto.getRandomValues(a); return [...a].map(b => b.toString(16).padStart(2,'0')).join(''); }
function qs(q?: Record<string,string>): string { return q ? Object.keys(q).map(k=>`${encodeURIComponent(k)}=${encodeURIComponent(q[k])}`).join('&') : ''; }

async function signed(method: 'GET'|'POST', subPath: string, opts: { query?: Record<string,string>; body?: any } = {}): Promise<{status:number; data:any}> {
  const query = qs(opts.query);
  const bodyStr = opts.body !== undefined ? JSON.stringify(opts.body) : '';
  const ts = Math.floor(Date.now()/1000).toString();
  const non = nonce();
  const canonical = [method, subPath, query, ts, non, await sha256(bodyStr)].join('\n');
  const sig = await hmac(APP_SECRET, canonical);
  const headers: Record<string,string> = { 'X-App-Id': APP_ID, 'X-Timestamp': ts, 'X-Nonce': non, 'X-Signature': sig };
  if (bodyStr) headers['Content-Type'] = 'application/json';
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) headers['Authorization'] = 'Bearer ' + tok;
  const url = ENDPOINT_V2 + subPath + (query ? '?' + query : '');
  const r = await fetch(url, { method, headers, body: bodyStr || undefined });
  let data: any = null; try { data = JSON.parse(await r.text()); } catch {}
  if (r.status === 401) { /* don't auto-clear — admin may be in 2fa step */ }
  return { status: r.status, data };
}

export const adminStore = {
  token: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); },
  loggedIn: () => !!localStorage.getItem(TOKEN_KEY),
  setUser: (u:any) => localStorage.setItem(USER_KEY, JSON.stringify(u)),
  user: (): any => { try { return JSON.parse(localStorage.getItem(USER_KEY)||'null'); } catch { return null; } },
};

export const adminApi = {
  async login(username:string, password:string) {
    const { status, data } = await signed('POST','/admin/login',{body:{username,password}});
    return { status, data };
  },
  async verify2fa(username:string, code:string) {
    const { status, data } = await signed('POST','/admin/verify-2fa',{body:{username, code}});
    if (status===200 && data?.token) { adminStore.setToken(data.token); if (data.admin) adminStore.setUser(data.admin); }
    return { status, data };
  },
  logout() { signed('POST','/admin/logout').catch(()=>{}); adminStore.clear(); },
  async me() { return signed('GET','/admin/me'); },
  async stats() { return signed('GET','/admin/stats'); },
  async users(params: Record<string,string>={}) { return signed('GET','/admin/users',{query: params}); },
  async userDetail(id:string|number) { return signed('GET',`/admin/users/${id}`); },
  async banUser(id:string|number, reason='Violation') { return signed('POST',`/admin/users/${id}/ban`,{body:{reason}}); },
  async resetTraffic(id:string|number) { return signed('POST',`/admin/users/${id}/reset-traffic`); },
  async extendPlan(id:string|number, years=1) { return signed('POST',`/admin/users/${id}/extend`,{body:{years}}); },
  async invoices(params: Record<string,string>={}) { return signed('GET','/admin/invoices',{query: params}); },
  async coupons() { return signed('GET','/admin/coupons'); },
  async createCoupon(body:any) { return signed('POST','/admin/coupons',{body}); },
  async deleteCoupon(id:string|number) { return signed('POST',`/admin/coupons/${id}/delete`); },
  async contacts(params: Record<string,string>={}) { return signed('GET','/admin/contacts',{query: params}); },
  async securityEvents() { return signed('GET','/admin/security-events'); },
  async sendEmail(to:string,subject:string,message:string) { return signed('POST','/admin/email/send',{body:{to,subject,message}}); },
  async broadcastEmail(subject:string,message:string) { return signed('POST','/admin/email/broadcast',{body:{subject,message}}); },
  // Servers
  async servers() { return signed('GET','/admin/servers'); },
  async createServer(body:any) { return signed('POST','/admin/servers',{body}); },
  async updateServer(id:string|number, body:any) { return signed('POST',`/admin/servers/${id}/update`,{body}); },
  async toggleServer(id:string|number) { return signed('POST',`/admin/servers/${id}/toggle`); },
  async deleteServer(id:string|number) { return signed('POST',`/admin/servers/${id}/delete`); },
  // Payments
  async payments(params: Record<string,string>={}) { return signed('GET','/admin/payments',{query: params}); },
  async markPaid(id:string|number) { return signed('POST',`/admin/payments/${id}/paid`); },
  async deletePayment(id:string|number) { return signed('POST',`/admin/payments/${id}/delete`); },
  // Refunds
  async refunds(params: Record<string,string>={}) { return signed('GET','/admin/refunds',{query: params}); },
  async processRefund(id:string|number, action:'approve'|'reject') { return signed('POST',`/admin/refunds/${id}/process`,{body:{action}}); },
  // Alerts
  async alerts() { return signed('GET','/admin/alerts'); },
  async createAlert(body:any) { return signed('POST','/admin/alerts',{body}); },
  async deleteAlert(id:string|number) { return signed('POST',`/admin/alerts/${id}/delete`); },
  async alertReads(alertId?: number) { return signed('GET','/admin/alerts/reads', { query: alertId ? { alert_id: String(alertId) } : {} }); },
  // Settings
  async settings() { return signed('GET','/admin/settings'); },
  async updateSettings(body:any) { return signed('POST','/admin/settings',{body}); },
  // Trash
  async trash() { return signed('GET','/admin/trash'); },
  async trashRestore(id:string|number) { return signed('POST',`/admin/trash/${id}/restore`); },
  async trashPurge(id:string|number) { return signed('POST',`/admin/trash/${id}/purge`); },
  // Apps
  async apps() { return signed('GET','/admin/apps'); },
  async deleteApp(id:string|number) { return signed('POST',`/admin/apps/${id}/delete`); },
  // Reviews
  async submissions() { return signed('GET','/admin/submissions'); },
  async submissionAction(id:string|number, action:string, admin_note='') { return signed('POST',`/admin/submissions/${id}/action`,{body:{action, admin_note}}); },
  async appeals() { return signed('GET','/admin/appeals'); },
  async resolveAppeal(id:string|number, action:string, admin_response='') { return signed('POST',`/admin/appeals/${id}/resolve`,{body:{action, admin_response}}); },
  // Logs
  async debugLogs() { return signed('GET','/admin/logs/debug'); },
  async debugTail(params: Record<string,string>) { return signed('GET','/admin/logs/debug/tail',{query: params}); },
  async deleteDebugLog(body:any) { return signed('POST','/admin/logs/debug/delete',{body}); },
  async testerLogs() { return signed('GET','/admin/logs/testers'); },
  async liveSessions(params: Record<string,string>={}) { return signed('GET','/admin/logs/live',{query: params}); },
  async liveContent(params: Record<string,string>) { return signed('GET','/admin/logs/live/content',{query: params}); },
  async deleteLive(body:any) { return signed('POST','/admin/logs/live/delete',{body}); },
  // Traffic & AI & extended users
  async traffic() { return signed('GET','/admin/traffic'); },
  async aiChat(message:string, history:any[] = []) { return signed('POST','/admin/ai/chat',{body:{message, history}}); },
  async togglePro(id:string|number) { return signed('POST',`/admin/users/${id}/pro`); },
  async setPlan(id:string|number, plan:string) { return signed('POST',`/admin/users/${id}/plan`,{body:{plan}}); },
  async setTester(id:string|number, active:boolean) { return signed('POST',`/admin/users/${id}/tester`,{body:{active: active?1:0}}); },
  async deleteUser(id:string|number) { return signed('POST',`/admin/users/${id}/delete`); },
  async addYears(id:string|number, years:number) { return signed('POST',`/admin/users/${id}/add-years`,{body:{years}}); },
};
